import { useLinkedProjectIntegrations } from '@hooks/useLinkedProjectIntegrations'
import { FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavItem } from '../../components/NavItem'
import { Column } from './Column'

interface Props {
  autoSelect: boolean
  displaySearch?: boolean
  heading?: string
  onItemClick: (item: { id: number; name: string }) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  projectId: number
  searchValue?: string
  shown: boolean
}

export const ProjectIntegrationsColumn: FC<Props> = memo(
  ({
    autoSelect,
    displaySearch,
    heading,
    onItemClick,
    onSearchChange,
    onSearchToggle,
    projectId,
    searchValue = '',
    shown,
  }) => {
    const { t } = useTranslation()

    const projectIntegrationsResult = useLinkedProjectIntegrations({
      enabled: shown,
      projectId,
      onSuccess: (projectIntegrations) => {
        if (!autoSelect) return
        if (projectIntegrations.length > 1) return

        onItemClick(projectIntegrations[0])
      },
    })

    const items = useMemo(
      () => projectIntegrationsResult.data ?? [],
      [projectIntegrationsResult.data],
    )

    const filteredItems = useMemo(() => {
      if (!searchValue?.trim()) return items

      return items.filter((item) =>
        item.name.toLowerCase().includes(searchValue.trim().toLowerCase()),
      )
    }, [items, searchValue])

    if (!shown) return null

    return (
      <Column
        heading={heading}
        displaySearch={displaySearch}
        isFetching={
          projectIntegrationsResult.isFetching ||
          projectIntegrationsResult.isLoading
        }
        isInitiallyLoading={projectIntegrationsResult.isLoading}
        items={filteredItems}
        loadMoreItems={() => undefined}
        activeFilterEnabled={false}
        onSearchToggle={onSearchToggle}
        onSearchChange={onSearchChange}
        renderItem={(item) => (
          <NavItem
            key={`${item.id ?? ''}-${item.name}`}
            label={item.name}
            onClick={() => onItemClick(item)}
            showImage={false}
            skip={false}
          />
        )}
        searchValue={searchValue}
        subheading={t('features.timeLogging.taskSelection.linkedIntegrations')}
        totalItems={filteredItems.length}
      />
    )
  },
)

ProjectIntegrationsColumn.displayName = 'ProjectIntegrationsColumn'
